import Typography from '../constants/Typography';
import {palette} from '../theme/colorGroups/palette.colors';

/**
 * Styles that are IDENTICAL between confirmEndModal.styles.ts and
 * ConfirmpartsRequest.styles.ts. Extracted here to eliminate duplication.
 * Both files spread this helper and only define their own overrides.
 */
export const createConfirmPartsBaseStyles = (theme: any) => ({
  container: {
    flex: 1,
    justifyContent: Typography.textAlign.centerAlign,
    alignItems: Typography.textAlign.centerAlign,
    backgroundColor: palette.opacity.modalBackdrop,
  },
  separatorLine: {
    height: Typography.height.height1,
    backgroundColor: theme.noteHeaderSeparator,
  },
  header: {
    flexDirection: 'row' as const,
    justifyContent: Typography.contentAlign.spaceBetween,
    alignItems: Typography.textAlign.centerAlign,
  },
  subWoHeader: {
    flexDirection: 'column' as const,
    gap: Typography.gap.gap8,
  },
  title: {
    fontSize: Typography.fontSize.fontSize24,
    fontWeight: Typography.fontWeight.semiBold,
    fontFamily: Typography.fontFamily.rethinkSans,
    color: theme.notedModalMainTitle,
    lineHeight: Typography.lineHeight.lineHeight36,
    letterSpacing: 0,
  },
  subTitle: {
    fontSize: Typography.fontSize.fontSize12,
    fontWeight: Typography.fontWeight.regular,
    fontFamily: Typography.fontFamily.inter,
    color: theme.confirmPartsSubTitle,
  },
  partsRequest: {
    flexDirection: 'column' as const,
    borderWidth: Typography.borderWidth.borderWidth1,
    borderColor: theme.noteSectionBorder,
    borderRadius: Typography.radius.radius8,
    padding: Typography.padding.padding24,
    gap: Typography.gap.gap16,
    flex: 1,
  },
  tileContainer: {
    flexDirection: 'row' as const,
    gap: Typography.gap.gap24,
  },
  titleContainer: {
    flexDirection: 'column' as const,
    gap: Typography.gap.gap12,
  },
  bottomContainer: {
    flexDirection: 'row' as const,
    gap: Typography.gap.gap16,
    alignItems: Typography.textAlign.centerAlign,
  },
  bottomRightContainer: {
    flexDirection: 'row' as const,
    gap: Typography.gap.gap4,
    alignItems: Typography.textAlign.centerAlign,
  },
  bottomRightTextContainer: {
    paddingVertical: Typography.padding.padding4,
    paddingHorizontal: Typography.padding.padding12,
    alignItems: Typography.textAlign.centerAlign,
    borderColor: theme.bottomTabsBorder,
    borderWidth: Typography.borderWidth.borderWidth1,
    borderRadius: Typography.radius.radius8,
  },
  bottomLeftContainer: {
    flexDirection: 'row' as const,
    gap: Typography.gap.gap4,
  },
  selectedCard: {
    borderColor: theme.selectedtabBorder,
    backgroundColor: theme.selectedtabBGColor,
  },
  partsTitleText: {
    fontSize: Typography.fontSize.fontSize18,
    fontFamily: Typography.fontFamily.inter,
    fontWeight: Typography.fontWeight.semiBold,
    lineHeight: Typography.lineHeight.lineHeight18,
    color: theme.partName,
  },
  partsSubTitleText: {
    fontSize: Typography.fontSize.fontSize16,
    fontFamily: Typography.fontFamily.inter,
    fontWeight: Typography.fontWeight.regular,
    lineHeight: Typography.lineHeight.lineHeight20,
    color: theme.reqQuantity,
  },
  partsNumberTextKey: {
    fontSize: Typography.fontSize.fontSize14,
    fontFamily: Typography.fontFamily.inter,
    fontWeight: Typography.fontWeight.medium,
    lineHeight: Typography.lineHeight.lineHeight20,
    color: theme.partNumberKey,
  },
  partsIdTextKey: {
    fontSize: Typography.fontSize.fontSize14,
    fontFamily: Typography.fontFamily.inter,
    fontWeight: Typography.fontWeight.medium,
    lineHeight: Typography.lineHeight.lineHeight20,
    color: theme.partIdKey,
    textAlign: Typography.textAlign.centerAlign,
  },
  noteLogText: {
    fontSize: Typography.fontSize.fontSize14,
    fontFamily: Typography.fontFamily.inter,
    fontWeight: Typography.fontWeight.semiBold,
    lineHeight: Typography.lineHeight.lineHeight16,
    color: theme.createdKey,
  },
});


/**
 * Base modal style definitions shared across multiple modal components
 * (NoteDetailModal, AddNewNoteModal, IndirectActivityModal,
 *  ChangeRepairReasonModal, ChangeWOStatusModal, ConfirmpartsRequest,
 *  PartsOwnRequestModal, etc.).
 * Returns a plain object (not a StyleSheet) so it can be spread into each
 * modal's own StyleSheet.create call.
 */
export const createModalBaseStyles = (theme: any) => ({
  header: {
    flexDirection: 'row' as const,
    justifyContent: Typography.contentAlign.spaceBetween,
    alignItems: Typography.textAlign.centerAlign,
    paddingHorizontal: Typography.padding.padding24,
  },
  title: {
    fontSize: Typography.fontSize.fontSize24,
    fontWeight: Typography.fontWeight.semiBold,
    fontFamily: Typography.fontFamily.rethinkSans,
    color: theme.notedModalMainTitle,
    lineHeight: Typography.lineHeight.lineHeight36,
  },
  noteLogText: {
    fontSize: Typography.fontSize.fontSize14,
    fontFamily: Typography.fontFamily.inter,
    fontWeight: Typography.fontWeight.semiBold,
    lineHeight: Typography.lineHeight.lineHeight16,
    color: theme.createdKey,
  },
  selectedCard: {
    borderColor: theme.selectedtabBorder,
    backgroundColor: theme.selectedtabModalBGColor,
  },
  noteLogTitleText: {
    fontSize: Typography.fontSize.fontSize14,
    fontFamily: Typography.fontFamily.inter,
    fontWeight: Typography.fontWeight.medium,
    lineHeight: Typography.lineHeight.lineHeight20,
    color: theme.woStatusTitle,
    textAlign: Typography.textAlign.centerAlign,
  },
  cancelContainerStyle: {
    backgroundColor: 'transparent',
    borderColor: theme.cancelCTABorder,
    borderWidth: Typography.borderWidth.borderWidth1,
  },
  cancelCustomTextStyle: {
    color: theme.cancelCTAText,
    fontSize: Typography.fontSize.fontSize14,
    fontWeight: Typography.fontWeight.medium,
    fontFamily: Typography.fontFamily.inter,
  },
  saveContainerStyle: {
    backgroundColor: theme.listeningEnabledCTABG,
    borderColor: theme.cancelCTABorder,
    borderWidth: Typography.borderWidth.borderWidth1,
  },
  saveCustomTextStyle: {
    color: theme.listeningEnabledCTAText,
    fontSize: Typography.fontSize.fontSize14,
    fontWeight: Typography.fontWeight.medium,
    fontFamily: Typography.fontFamily.inter,
  },
  disabledSaveContainerStyle: {
    backgroundColor: theme.saveNoteCTADisabledBG,
    borderColor: theme.cancelCTABorder,
    borderWidth: Typography.borderWidth.borderWidth1,
  },
  disabledSaveCustomTextStyle: {
    color: theme.saveNoteCTADisabledText,
    fontSize: Typography.fontSize.fontSize14,
    fontWeight: Typography.fontWeight.medium,
    fontFamily: Typography.fontFamily.inter,
  },
});

/**
 * Shared layout styles for ChangeRepairReasonModal and ChangeWOStatusModal.
 * Captures styles common to both so changes only need to be made in one place.
 * The `noteLog` entry intentionally omits maxWidth; ChangeWOStatusModal
 * overrides it with maxWidth: Typography.width.width360.
 */
export const createChangeModalBaseStyles = (theme: any) => ({
  container: {
    flex: 1,
    justifyContent: Typography.textAlign.centerAlign,
    alignItems: Typography.textAlign.centerAlign,
    backgroundColor: palette.opacity.modalBackdrop,
  },
  modal: {
    backgroundColor: theme.notedModalBG,
    paddingVertical: Typography.padding.padding24,
    borderColor: theme.notesModalBorder,
    borderWidth: Typography.borderWidth.borderWidth1,
    borderRadius: Typography.radius.radius12,
    gap: Typography.gap.gap24,
    justifyContent: Typography.textAlign.centerAlign,
    maxHeight: Typography.height.height600,
    width: Typography.width.width807,
  },
  title: {
    fontSize: Typography.fontSize.fontSize24,
    fontWeight: Typography.fontWeight.medium,
    fontFamily: Typography.fontFamily.inter,
    color: theme.notedModalMainTitle,
    lineHeight: Typography.lineHeight.lineHeight36,
  },
  content: {
    flexDirection: 'column' as const,
    paddingHorizontal: Typography.padding.padding24,
    gap: Typography.gap.gap36,
  },
  applyToContainer: {
    gap: Typography.gap.gap8,
  },
  listContainer: {justifyContent: Typography.contentAlign.spaceBetween},
  label: {
    fontSize: Typography.fontSize.fontSize14,
    fontWeight: Typography.fontWeight.medium,
    color: theme.changeModalStubTitle,
  },
  mainLabel: {
    fontSize: Typography.fontSize.fontSize14,
    fontWeight: Typography.fontWeight.medium,
    color: theme.changeModalStubMainTitle,
  },
  subLabel: {
    fontSize: Typography.fontSize.fontSize24,
    fontWeight: Typography.fontWeight.semiBold,
    color: theme.woStatusDesc,
  },
  noteLog: {
    flexDirection: 'column' as const,
    borderWidth: Typography.borderWidth.borderWidth1,
    borderColor: theme.noteSectionBorder,
    borderRadius: Typography.radius.radius8,
    paddingVertical: Typography.padding.padding12,
    flex: 1,
  },
  footerButtons: {
    flexDirection: 'row' as const,
    justifyContent: Typography.contentAlign.flexEnd,
    gap: Typography.gap.gap16,
    paddingHorizontal: Typography.padding.padding24,
  },
});